import { EditIcon, SettingsIcon } from '@mm/icons';
import classnames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { getPercentage } from '@mm/utils';
import { getDeviceInfo } from '../../../../../common/hooks/use-device-info';
import { useGreenCueMarkers } from '../../../../../common/hooks/use-green-cue-markers';
import '../../../../../common/scss/input.scss';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../../common/widgets/common/PathGraph/PathGraph';
import { useMainMenuCRUDActions } from '@mm/state/actions/MainMenuCRUDActions';
import pageCRUDActions from '@mm/state/actions/PageCRUDActions';
import PageModel, { PageKind } from '@mm/state/model/PageModel';
import { DefaultResponseType } from '@mm/state/model/ChatbotModel';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import {
  defaultPageSelector,
  pageEntityByIdSelector,
  selectPages,
} from '@mm/state/selectors/pageSelectors';
import modalActions from '@mm/state/modules/modalsState/actions';
import { hasDeactivatedNumberSelector } from '@mm/state/selectors/twillio';
import { widgetsByDialogueReselectSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { DialogueHeaderWarningAlert } from '../../../common/components/DialogueHeaderWarningAlert/DialogueHeaderWarningAlert';
import { NeedHelpLabel } from '../../../common/components/NeedHelpLabel/NeedHelpLabel';
import { StartBotsDialogueEditor } from '../../../startBotPromoters/common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import './PageEditorPane.pcss';
import TestBotButton from './TestBotButton/TestBotButton';

export const DIALOGUE_MAX_NAME = 50;

const getAbandonedPercentage = (dialogue: PageModel | undefined) => {
  if (dialogue) {
    const { abandonedCount, viewsCount } = dialogue;
    const impressions = `${viewsCount} Impressions`;

    if (abandonedCount && viewsCount) {
      const percentage = getPercentage(abandonedCount, viewsCount);

      return `${impressions} (${percentage}% Bounce Rate)`;
    }

    if (viewsCount) {
      return impressions;
    }
  }

  return '0 Impressions';
};

const PageEditorPane = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  // const { match } = useRouter<{ chatbotId: string; pageId: string }>();
  const { chatbotId, pageId } = useParams<{
    chatbotId: string;
    pageId?: string;
  }>();
  const [isViewMode, setIsViewMode] = useState(true);
  // const activeDialogueId = Number(match.params.pageId);
  const activeDialogueId = Number(pageId);
  const dialogues = useSelector((state: StoreState) => state.page.entities);
  const activeDialogue = dialogues.find(
    dialogue => dialogue.id === activeDialogueId,
  );
  // const chatbotId = match.params.chatbotId;
  const searchToken = activeDialogue ? activeDialogue.searchToken : undefined;
  const name = activeDialogue ? activeDialogue.name : '';
  const isPageFixed = Boolean(
    activeDialogue &&
      activeDialogue.kind &&
      activeDialogue.kind !== PageKind.StartBot,
  );
  const [localName, setLocalName] = useState(name);
  const abandonedPercentageText = getAbandonedPercentage(activeDialogue);
  const mainMenuCRUDActions = useMainMenuCRUDActions();
  const menuItems = useSelector((state: StoreState) => state.mainMenu.entities);
  const widgets = useSelector(widgetsByDialogueReselectSelector);

  const pathGraph = useNewGraphInstance(activeDialogueId);
  const { isSmsActivated } = useGreenCueMarkers();
  const hasDeactivatedNumber = useSelector(hasDeactivatedNumberSelector);
  const { isMobile } = getDeviceInfo();

  const activeChatbotEntity = useSelector(activeChatbotSelector);
  const defaultDialogue = useSelector(defaultPageSelector);
  const ignoreDefaultDialogue =
    activeChatbotEntity.noMatchResponse === DefaultResponseType.Ignore;
  const showDefaultDealoguesIgnoredWarning =
    ignoreDefaultDialogue && defaultDialogue?.id === activeDialogueId;

  useEffect(() => {
    if (menuItems.length === 0 && chatbotId) {
      mainMenuCRUDActions.retrieveAll(Number(chatbotId));
    }
  }, [chatbotId]);

  useEffect(() => {
    setLocalName(name);
  }, [name]);

  useEffect(() => {
    setIsViewMode(true);
  }, [activeDialogueId]);

  const saveName = () => {
    if (activeDialogue) {
      const updatedPage = {
        ...activeDialogue,
        name: localName,
      };

      dispatch(pageCRUDActions.update(updatedPage));
      setIsViewMode(true);
    }
  };

  const inputClassList = classnames('page-editor__page-name', {
    'page-editor__page-name-view-mode': isViewMode,
  });

  const openSettingsModal = () => {
    if (activeDialogue !== undefined) {
      dispatch(modalActions.openDialogueSettingsModal(activeDialogue.id));
    }
  };

  const onClickCancel = () => {
    setLocalName(name);
    setIsViewMode(true);
  };

  const widgetsToExclude = useMemo(() => [], []);

  return (
    <div className="page-editor__outer-container">
      <div className="page-editor__header">
        <div className="page-editor__header-top">
          <div className="page-editor__settings-icon--container">
            <SettingsIcon
              className="page-editor__settings-icon"
              onClick={openSettingsModal}
            />
          </div>
          <div className="page-editor__header-left">
            {isViewMode ? (
              <>
                <div className="page-editor__dialogue-name-preview">
                  {localName}
                </div>

                {isViewMode && !isPageFixed && (
                  <EditIcon onClick={() => setIsViewMode(!isViewMode)} />
                )}
              </>
            ) : (
              <input
                className={inputClassList}
                value={localName}
                maxLength={DIALOGUE_MAX_NAME}
                onChange={e => setLocalName(e.target.value)}
                disabled={isPageFixed || isViewMode}
              />
            )}

            {!isViewMode && (
              <>
                <PrimaryButton
                  color={ButtonColor.invertedRed}
                  className="page-editor__cancel-button"
                  onClick={onClickCancel}
                >
                  Cancel
                </PrimaryButton>

                <PrimaryButton
                  className="page-editor__save-button"
                  onClick={saveName}
                  disabled={!localName.length}
                >
                  Save
                </PrimaryButton>
              </>
            )}
          </div>
          {showDefaultDealoguesIgnoredWarning && isViewMode && (
            <DialogueHeaderWarningAlert
              alertClassName="page-editor__header-alert"
              alertIsVisible={showDefaultDealoguesIgnoredWarning}
              beforeLinkText="Your bot is set to ignore unanswered questions, so this default dialogue will never get triggered"
              showLink={false}
              showAfterLinkText={false}
            />
          )}
          {!showDefaultDealoguesIgnoredWarning && isViewMode && (
            <DialogueHeaderWarningAlert alertClassName="page-editor__header-alert" />
          )}
          <div className="page-editor__header-right">
            <NeedHelpLabel
              link="https://mobilemonkey.com/help/article/kp3ddtl4lb-bot-builder-overview"
              iconOnly={isMobile}
            />
            <div className="page-editor__abandoned">
              {abandonedPercentageText}
            </div>
            {searchToken && <TestBotButton searchToken={searchToken} />}
          </div>
        </div>
      </div>

      <PathGraphContext.Provider value={pathGraph}>
        <StartBotsDialogueEditor
          widgets={widgets}
          dialogueId={activeDialogueId}
          widgetsToExclude={widgetsToExclude}
          chatbotId={Number(chatbotId)}
          showCountByChannel={isSmsActivated && !hasDeactivatedNumber}
          className="page-editor__startbots-dialogue-editor"
          platforms={['facebook']}
        />
      </PathGraphContext.Provider>
    </div>
  );
};

export default PageEditorPane;
